/**
 * <sso-handoff>
 *
 * Reads the auction site's return address off the query string, checks it
 * against the allowed hosts, and sends the shopper back with the signed
 * reference Liquid rendered into `data-reference`.
 *
 * WHY THE RETURN ADDRESS IS READ HERE AND NOT IN LIQUID
 * -----------------------------------------------------
 * Liquid cannot see a query parameter. There is no `request.page_url` —
 * measured on this store, it renders empty — so the proof of concept's
 * server-side parsing always produced nil and its browser fallback was doing
 * the whole job. This is that fallback, promoted to the only path.
 *
 * THE HOST CHECK IS THE SECURITY BOUNDARY
 * ---------------------------------------
 * `ssoReference` authenticates the shopper to whoever receives it, so sending
 * it to an attacker-chosen host would hand them a valid session elsewhere.
 * Anything not on the allow list stops here: no redirect, no reference, an
 * error message and a link out.
 *
 * Matched on the parsed `hostname`, never on a substring of the URL. A
 * `startsWith`/`includes` test passes `https://evil.test/?x=auction-milb1stg1
 * .oc1.servicecdi.com`, and a bare `endsWith('.example.com')` without the
 * leading dot passes `evilexample.com`.
 */

const SIGNING = '[data-sso-signing]';
const SIGNED_OUT = '[data-sso-signed-out]';
const ERROR = '[data-sso-error]';
const LOGIN_LINK = '[data-sso-login-link]';

if (!customElements.get('sso-handoff')) {
  customElements.define(
    'sso-handoff',
    class SsoHandoff extends HTMLElement {
      connectedCallback() {
        const ret = this.returnUrl;

        /* USED ONCE, THEN FORGOTTEN — as the POC does.
           The stored copy exists only to survive a login round trip. Left
           behind, it outlives the journey it belongs to: a later visit to this
           page with no `isynRetUrl` would find the old address and send the
           shopper to an auction page they did not ask for. Cleared on read,
           whichever source answered. */
        this.forgetReturn();

        // Already signed in and configured: hand off, or refuse.
        if (this.reference) {
          if (!ret || !this.isAllowed(ret)) {
            this.show(ERROR);
            return;
          }

          this.show(SIGNING);
          window.location.replace(this.handoffUrl(ret));
          return;
        }

        // Signed out. The login round trip is off by default on this store —
        // see the section's comment — so the shopper is asked to sign in
        // rather than bounced somewhere that strands them.
        this.show(SIGNED_OUT);

        if (ret && this.isAllowed(ret)) {
          this.rememberReturn(ret);
          if (this.hasAttribute('data-login-return')) {
            window.location.href = this.loginUrl(ret);
          }
        }
      }

      /* ---------------------------------------------------------------- */

      get reference() {
        return this.dataset.reference || '';
      }

      get returnParam() {
        return this.dataset.returnParam || 'isynRetUrl';
      }

      /* The address the auction site asked to be returned to. Taken from the
         query string, then from sessionStorage — the second covers the shopper
         coming back from a login that dropped the parameter. */
      get returnUrl() {
        const fromQuery = new URLSearchParams(window.location.search).get(this.returnParam);
        if (fromQuery) return fromQuery;

        try {
          return sessionStorage.getItem(this.returnParam) || '';
        } catch (error) {
          // Private browsing, or storage blocked. Not fatal: the query string
          // is the normal path and this is only the fallback.
          return '';
        }
      }

      get allowedHosts() {
        return (this.dataset.allowedHosts || '')
          .split(',')
          .map((host) => host.trim().toLowerCase())
          .filter(Boolean);
      }

      /**
       * @param {string} candidate - The return address to vet
       * @returns {boolean} true only for an exact host match or a true subdomain
       */
      isAllowed(candidate) {
        let hostname;

        try {
          hostname = new URL(candidate).hostname.toLowerCase();
        } catch (error) {
          // Not a parseable absolute URL — a relative path, or junk.
          return false;
        }

        return this.allowedHosts.some((allowed) => hostname === allowed || hostname.endsWith(`.${allowed}`));
      }

      handoffUrl(ret) {
        const url = new URL(ret);
        // `searchParams.set` encodes the value and replaces rather than
        // duplicates a key the auction site already put there — which hand-built
        // string concatenation gets wrong whenever `ret` arrives with a query.
        url.searchParams.set('ssoAttempt', 'Y');
        url.searchParams.set('ssoReference', this.reference);
        return url.toString();
      }

      /**
       * Where to send a signed-out shopper, and how to get them back here.
       *
       * THE `view` PARAMETER HAS TO SURVIVE. This section can be reached two
       * ways: as a page's assigned template, or as `?view=sso` on any page.
       * Under `view`, the path alone is an ordinary page — come back to
       * `/pages/x?isynRetUrl=…` without it and the shopper lands on that page's
       * normal content, with no handoff and no explanation.
       *
       * Liquid cannot help here: there is no `request.view`, and `request.path`
       * carries no query string. The browser can see it, so it is read and
       * re-attached here.
       */
      /**
       * Where to come back to after signing in — path, query and all.
       *
       * THE `view` PARAMETER HAS TO SURVIVE. This section is reachable two
       * ways: as a page's assigned template, or as `?view=sso` on any page.
       * Under `view`, the path alone is an ordinary page — return to
       * `/pages/x?isynRetUrl=…` without it and the shopper lands on that page's
       * normal content, with no handoff and no explanation.
       *
       * Liquid cannot help: there is no `request.view`, and `request.path`
       * carries no query string. The browser can see it, so it is re-attached
       * here.
       */
      returnTarget(ret) {
        const back = new URL(this.dataset.handoffPath, window.location.origin);

        const view = new URLSearchParams(window.location.search).get('view');
        if (view) back.searchParams.set('view', view);
        back.searchParams.set(this.returnParam, ret);

        return back;
      }

      /**
       * STRAIGHT TO THE HOSTED ACCOUNT PAGE, NOT THROUGH THE STORE'S OWN ROUTE.
       *
       * `routes.account_login_url` discards the return destination: measured on
       * this store, `return_to` and `return_url` on both `/account/login` and
       * `/customer_authentication/redirect` all arrive at
       * `shopify.com/<id>/account?buyer_flags=…` with nothing else attached.
       * Going through it means the shopper logs in and never comes back.
       *
       * New customer accounts read `return_url` on the hosted URL itself, so
       * this skips the route that strips it. An absolute URL, because the
       * hosted page is a different origin and a path would be meaningless
       * there.
       */
      loginUrl(ret) {
        const login = new URL(this.dataset.accountUrl, window.location.origin);
        login.searchParams.set('return_url', this.returnTarget(ret).toString());
        return login.toString();
      }

      rememberReturn(ret) {
        try {
          sessionStorage.setItem(this.returnParam, ret);
        } catch (error) {
          // Nothing to do: the query string path still works.
        }
      }

      forgetReturn() {
        try {
          sessionStorage.removeItem(this.returnParam);
        } catch (error) {
          // Storage blocked — there was nothing stored to clear either.
        }
      }

      /* One state visible at a time. `hidden` rather than a class, so the
         messages stay out of the accessibility tree as well as off screen. */
      show(selector) {
        for (const match of [SIGNING, SIGNED_OUT, ERROR]) {
          const element = this.querySelector(match);
          if (element) element.hidden = match !== selector;
        }

        const link = this.querySelector(LOGIN_LINK);
        if (link) link.hidden = selector === SIGNING;
      }
    },
  );
}
